import { useState, useEffect } from 'react';
import { Monitor, Smartphone, Tablet, ArrowRight, Sparkles, Zap, Code, Palette, Bot, Settings } from 'lucide-react';

// Service-specific showcase configuration
const showcaseConfig = {
  'website-development': {
    title: 'Website Development',
    titleHighlight: 'in Action',
    subtitle: 'See how we transform ideas into stunning websites',
    accentColor: '#3B82F6',
    gradient: 'from-[#3B82F6] to-[#06B6D4]',
    icon: Code,
    projects: [
      {
        id: 1,
        title: 'E-Commerce Platform',
        client: 'RetailMax Inc.',
        description: 'Modern e-commerce with real-time inventory & payments',
        device: 'desktop',
        features: ['Shopping Cart', 'Payment Gateway', 'Inventory System', 'Admin Dashboard'],
        mockupType: 'ecommerce'
      },
      {
        id: 2,
        title: 'Corporate Website',
        client: 'TechCorp Solutions',
        description: 'Professional corporate site with custom CMS',
        device: 'desktop',
        features: ['Custom CMS', 'Blog System', 'Contact Forms', 'SEO Optimized'],
        mockupType: 'corporate'
      },
      {
        id: 3,
        title: 'Portfolio Website',
        client: 'Creative Studio',
        description: 'Animated portfolio with smooth transitions',
        device: 'tablet',
        features: ['Animations', 'Gallery', 'Case Studies', 'Contact Integration'],
        mockupType: 'portfolio'
      }
    ]
  },
  'app-development': {
    title: 'App Development',
    titleHighlight: 'Showcase',
    subtitle: 'Native performance, cross-platform excellence',
    accentColor: '#8B5CF6',
    gradient: 'from-[#8B5CF6] to-[#EC4899]',
    icon: Smartphone,
    projects: [
      {
        id: 1,
        title: 'Fitness Tracking App',
        client: 'FitLife Pro',
        description: 'iOS & Android app with real-time health metrics',
        device: 'mobile',
        features: ['Health Tracking', 'Workout Plans', 'Progress Charts', 'Social Features'],
        mockupType: 'fitness'
      },
      {
        id: 2,
        title: 'Food Delivery App',
        client: 'QuickBite',
        description: 'Real-time tracking and payment integration',
        device: 'mobile',
        features: ['Live Tracking', 'Payment Integration', 'Restaurant Listings', 'Reviews'],
        mockupType: 'delivery'
      },
      {
        id: 3,
        title: 'Banking App',
        client: 'SecureBank',
        description: 'Secure transactions with biometric auth',
        device: 'mobile',
        features: ['Biometric Auth', 'Transfers', 'Bill Pay', 'Account Management'],
        mockupType: 'banking'
      }
    ]
  },
  'uiux-design': {
    title: 'Design Process',
    titleHighlight: 'Revealed',
    subtitle: 'From wireframes to pixel-perfect interfaces',
    accentColor: '#F97316',
    gradient: 'from-[#F97316] to-[#EAB308]',
    icon: Palette,
    projects: [
      {
        id: 1,
        title: 'Dashboard UI Design',
        client: 'Analytics Pro',
        description: 'Modern dashboard with data visualization',
        device: 'desktop',
        features: ['Data Viz', 'Dark Mode', 'Responsive', 'Interactive Charts'],
        mockupType: 'dashboard'
      },
      {
        id: 2,
        title: 'Mobile App UI',
        client: 'SocialConnect',
        description: 'Social media app interface design',
        device: 'mobile',
        features: ['Stories', 'Feed Design', 'Profile System', 'Messaging UI'],
        mockupType: 'social'
      },
      {
        id: 3,
        title: 'Brand Identity',
        client: 'StartupX',
        description: 'Complete brand guidelines & assets',
        device: 'desktop',
        features: ['Logo Design', 'Color System', 'Typography', 'Brand Guidelines'],
        mockupType: 'brand'
      }
    ]
  },
  'ai-solutions': {
    title: 'AI Solutions',
    titleHighlight: 'in Production',
    subtitle: 'Intelligent automation transforming businesses',
    accentColor: '#10B981',
    gradient: 'from-[#10B981] to-[#3B82F6]',
    icon: Bot,
    projects: [
      {
        id: 1,
        title: 'Customer Service Bot',
        client: 'SupportHub',
        description: 'GPT-powered support reducing tickets by 70%',
        device: 'desktop',
        features: ['Natural Language', '24/7 Support', 'Multi-language', 'Analytics'],
        mockupType: 'chatbot'
      },
      {
        id: 2,
        title: 'Document Analysis',
        client: 'LegalTech Inc.',
        description: 'AI extracting insights from legal documents',
        device: 'desktop',
        features: ['OCR', 'Entity Extraction', 'Summarization', 'Search'],
        mockupType: 'document'
      },
      {
        id: 3,
        title: 'Sales Assistant',
        client: 'SalesForce Pro',
        description: 'AI agent qualifying leads automatically',
        device: 'desktop',
        features: ['Lead Scoring', 'Auto Outreach', 'CRM Integration', 'Reporting'],
        mockupType: 'sales'
      }
    ]
  },
  'business-tools': {
    title: 'Automation',
    titleHighlight: 'Success Stories',
    subtitle: 'Workflows that save 40+ hours per week',
    accentColor: '#EC4899',
    gradient: 'from-[#EC4899] to-[#8B5CF6]',
    icon: Settings,
    projects: [
      {
        id: 1,
        title: 'CRM Dashboard',
        client: 'SalesMax',
        description: 'Custom CRM with automated follow-ups',
        device: 'desktop',
        features: ['Pipeline View', 'Auto Follow-ups', 'Email Integration', 'Reports'],
        mockupType: 'crm'
      },
      {
        id: 2,
        title: 'Inventory System',
        client: 'WarehouseX',
        description: 'Real-time stock management & alerts',
        device: 'desktop',
        features: ['Stock Tracking', 'Low Stock Alerts', 'Barcode Scanning', 'Analytics'],
        mockupType: 'inventory'
      },
      {
        id: 3,
        title: 'HR Portal',
        client: 'TalentFirst',
        description: 'Employee management & onboarding automation',
        device: 'desktop',
        features: ['Onboarding', 'Time Tracking', 'Leave Management', 'Payroll'],
        mockupType: 'hr'
      }
    ]
  }
};

// Animated Device Mockup Component
const DeviceMockup = ({ device, mockupType, accentColor, isActive }) => {
  const getDeviceClasses = () => {
    switch (device) {
      case 'mobile':
        return 'w-[200px] h-[400px] rounded-[2.5rem] border-[6px]';
      case 'tablet':
        return 'w-[350px] h-[260px] rounded-[1.5rem] border-[5px]';
      default:
        return 'w-full max-w-[500px] h-[300px] rounded-xl border-[4px]';
    }
  };

  return (
    <div className={`relative ${getDeviceClasses()} border-gray-700 bg-gray-900 overflow-hidden shadow-2xl mx-auto`}
         style={{ boxShadow: isActive ? `0 25px 60px ${accentColor}30` : '0 10px 40px rgba(0,0,0,0.3)' }}>
      {/* Screen Content with Animated Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden">
        {/* Animated Grid Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(${accentColor}20 1px, transparent 1px), linear-gradient(90deg, ${accentColor}20 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }}></div>
        </div>

        {/* Animated UI Elements */}
        <div className="relative h-full p-3">
          {/* Header Bar */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
            </div>
            <div className="flex-1 h-5 bg-gray-700/50 rounded-full mx-2"></div>
          </div>

          {/* Animated Content Blocks */}
          <div className="space-y-2">
            {/* Hero Section */}
            <div 
              className="h-16 rounded-lg animate-pulse"
              style={{ background: `linear-gradient(135deg, ${accentColor}40, ${accentColor}20)` }}
            ></div>
            
            {/* Content Grid */}
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((i) => (
                <div 
                  key={i}
                  className="h-12 rounded-lg bg-gray-700/50 animate-pulse"
                  style={{ animationDelay: `${i * 0.2}s` }}
                ></div>
              ))}
            </div>

            {/* Data Rows */}
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2" style={{ animationDelay: `${i * 0.15}s` }}>
                <div 
                  className="w-8 h-8 rounded-lg animate-pulse"
                  style={{ background: `${accentColor}30` }}
                ></div>
                <div className="flex-1 space-y-1">
                  <div className="h-2 bg-gray-600/50 rounded-full w-3/4 animate-pulse"></div>
                  <div className="h-2 bg-gray-700/50 rounded-full w-1/2 animate-pulse"></div>
                </div>
                <div 
                  className="w-12 h-6 rounded-full animate-pulse"
                  style={{ background: `${accentColor}40` }}
                ></div>
              </div>
            ))}

            {/* Chart Area */}
            <div className="h-20 rounded-lg bg-gray-800/50 flex items-end justify-around p-2 gap-1">
              {[40, 65, 45, 80, 55, 70, 50].map((height, i) => (
                <div 
                  key={i}
                  className="w-4 rounded-t-sm transition-all duration-500"
                  style={{ 
                    height: isActive ? `${height}%` : '20%',
                    background: `linear-gradient(to top, ${accentColor}, ${accentColor}60)`,
                    transitionDelay: `${i * 0.1}s`
                  }}
                ></div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div 
          className="absolute top-4 right-4 w-12 h-12 rounded-full flex items-center justify-center animate-bounce"
          style={{ background: accentColor, animationDuration: '2s' }}
        >
          <Sparkles size={20} className="text-white" />
        </div>

        {/* Scanning Line Animation */}
        <div 
          className="absolute left-0 right-0 h-0.5 animate-scan"
          style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }}
        ></div>
      </div>

      {/* Mobile Notch */}
      {device === 'mobile' && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-20 h-5 bg-gray-700 rounded-b-xl"></div>
      )}
    </div>
  );
};

const AnimatedShowcase = ({ serviceId }) => {
  const config = showcaseConfig[serviceId] || showcaseConfig['website-development'];
  const [activeProject, setActiveProject] = useState(0);
  const IconComponent = config.icon;

  // Auto-rotate projects
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveProject((prev) => (prev + 1) % config.projects.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [config.projects.length]);

  const currentProject = config.projects[activeProject];

  return (
    <section className="py-20 px-[7.6923%]" style={{ background: 'var(--bg-card)' }}>
      {/* Add scanning animation keyframes */}
      <style>{`
        @keyframes scan {
          0% { top: 0; opacity: 0; }
          50% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .animate-scan {
          animation: scan 3s ease-in-out infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 border"
            style={{ background: `${config.accentColor}10`, borderColor: `${config.accentColor}30` }}
          >
            <Zap size={16} style={{ color: config.accentColor }} />
            <span style={{ color: config.accentColor }} className="text-sm font-semibold">Live Showcase</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
            {config.title}{' '}
            <span className={`bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}>
              {config.titleHighlight}
            </span>
          </h2>
          <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto">{config.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Showcase */}
          <div className="lg:col-span-2">
            <div 
              className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 overflow-hidden"
              style={{ boxShadow: `0 25px 60px ${config.accentColor}20` }}
            >
              {/* Background Glow */}
              <div 
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full blur-[100px] opacity-20"
                style={{ background: config.accentColor }}
              ></div>

              {/* Device Mockup */}
              <div className="relative flex items-center justify-center min-h-[350px]">
                <DeviceMockup 
                  device={currentProject.device}
                  mockupType={currentProject.mockupType}
                  accentColor={config.accentColor}
                  isActive={true}
                />
              </div>

              {/* Project Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/80 to-transparent">
                <div className="flex items-center gap-3 mb-2">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${config.accentColor}20` }}
                  >
                    <IconComponent size={20} style={{ color: config.accentColor }} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">{currentProject.title}</h3>
                    <p className="text-gray-400 text-sm">{currentProject.client}</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm mb-3">{currentProject.description}</p>
                
                {/* Features Tags */}
                <div className="flex flex-wrap gap-2">
                  {currentProject.features.map((feature, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 rounded-full text-xs font-medium"
                      style={{ background: `${config.accentColor}20`, color: config.accentColor }}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {/* Progress Indicators */}
              <div className="absolute top-6 right-6 flex gap-2">
                {config.projects.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveProject(idx)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      idx === activeProject ? 'w-6' : ''
                    }`}
                    style={{ 
                      background: idx === activeProject ? config.accentColor : 'rgba(255,255,255,0.3)'
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Project List */}
          <div className="space-y-4">
            <div className="bg-[var(--bg-dark)] border border-[var(--border)] rounded-2xl p-6">
              <h3 className="text-[var(--text-primary)] font-bold mb-4 flex items-center gap-2">
                <Sparkles size={18} style={{ color: config.accentColor }} />
                Featured Projects
              </h3>
              
              <div className="space-y-3">
                {config.projects.map((project, idx) => (
                  <button
                    key={project.id}
                    onClick={() => setActiveProject(idx)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-300 border ${
                      idx === activeProject 
                        ? 'border-transparent' 
                        : 'border-[var(--border)] hover:border-[var(--border-hover)]'
                    }`}
                    style={{
                      background: idx === activeProject ? `${config.accentColor}15` : 'transparent'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ 
                          background: idx === activeProject ? config.accentColor : 'var(--bg-card)'
                        }}
                      >
                        {project.device === 'mobile' ? (
                          <Smartphone size={18} className={idx === activeProject ? 'text-white' : 'text-[var(--text-muted)]'} />
                        ) : project.device === 'tablet' ? (
                          <Tablet size={18} className={idx === activeProject ? 'text-white' : 'text-[var(--text-muted)]'} />
                        ) : (
                          <Monitor size={18} className={idx === activeProject ? 'text-white' : 'text-[var(--text-muted)]'} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-semibold truncate ${idx === activeProject ? 'text-white' : 'text-[var(--text-primary)]'}`}>
                          {project.title}
                        </h4>
                        <p className="text-[var(--text-muted)] text-sm truncate">{project.client}</p>
                      </div>
                      {idx === activeProject && (
                        <div 
                          className="w-2 h-2 rounded-full animate-pulse"
                          style={{ background: config.accentColor }}
                        ></div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* CTA Card */}
            <div 
              className="p-6 rounded-2xl text-center"
              style={{ background: `linear-gradient(135deg, ${config.accentColor}20, ${config.accentColor}05)` }}
            >
              <h4 className="text-[var(--text-primary)] font-bold mb-2">Want Similar Results?</h4>
              <p className="text-[var(--text-muted)] text-sm mb-4">Let's discuss your project requirements</p>
              <a
                href="/book-consultation"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold transition-all hover:scale-105"
                style={{ background: `linear-gradient(135deg, ${config.accentColor}, ${config.accentColor}CC)` }}
              >
                Get Free Consultation
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnimatedShowcase;
